<template>
  <div class="dashboard">
    <!-- 工具栏 -->
    <el-card class="toolbar-card" shadow="never">
      <div class="toolbar-inner">
        <el-form :inline="true" :model="filters" size="small">
          <el-form-item>
            <SearchWhse ref="searchWhse" @search-whse-event="searchWhse(arguments)" perms="sys:user:view" type="primary"/>
          </el-form-item>
          <el-form-item>
            <StartEndDate ref="StartEndDate" @search-date-event="getSearchDate(arguments)" perms="sys:user:view" type="primary"/>
          </el-form-item>
          <el-form-item>
            <el-button size="small" type="primary" icon="el-icon-refresh" @click="refreshAll">
              {{ $t('ReportView.open') }}
            </el-button>
          </el-form-item>
        </el-form>
      </div>
    </el-card>

    <!-- 报表卡片网格 -->
    <div class="chart-grid">
      <el-card class="chart-card" shadow="hover" v-for="(card, idx) in chartCards" :key="idx">
        <div slot="header" class="chart-header">
          <div class="chart-title">
            <span class="chart-dot" :style="{ background: card.color }"></span>
            <span>{{ card.title }}</span>
          </div>
          <el-tag size="mini" :color="card.color" style="color:#fff;border:0;">{{ $t('ReportView.realTime') }}</el-tag>
        </div>
        <div :ref="card.ref" class="chart-box"></div>
      </el-card>

      <!-- 占位卡片 - 后续新增报表 -->
      <el-card class="chart-card placeholder-card" shadow="hover" v-if="chartCards.length % 2 === 0">
        <div slot="header" class="chart-header">
          <div class="chart-title">
            <span class="chart-dot" style="background:#ddd"></span>
            <span style="color:#bbb">{{ $t('ReportView.more') }}</span>
          </div>
        </div>
        <div class="placeholder-body">
          <i class="el-icon-plus placeholder-icon"></i>
          <p class="placeholder-text">{{ $t('ReportView.placeholder') }}</p>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import echarts from 'echarts';
import SearchWhse from "@/views/Core/SearchWhse"
import StartEndDate from "@/views/Core/StartEndDate"

const CHART_COLORS = [
  '#409EFF', '#67C23A', '#E6A23C', '#F56C6C',
  '#909399', '#36CFC9', '#B37FEB', '#F56C6C'
];

export default {
  name: "Dashboard",
  components: { SearchWhse, StartEndDate },
  data() {
    return {
      filters: { whId: '', startDate: '', endDate: '' },
      size: 'small',
      intervals: [],
      chartInstances: {},

      chartCards: [
        { ref: 'orderStatusChart', title: this.$t ? this.$t('Dashboard.orderStatus') : '订单状态', color: CHART_COLORS[0] },
        { ref: 'locationStatusChart', title: this.$t ? this.$t('Dashboard.locationUtilization') : '库位利用率', color: CHART_COLORS[1] },
        { ref: 'expiryDateItemsChart', title: this.$t ? this.$t('Dashboard.expiryItems') : '临期产品数量', color: CHART_COLORS[2] },
      ],
    }
  },
  methods: {
    searchWhse(data) { this.filters.whId = data[0] },
    getSearchDate(data) { this.filters.startDate = data[0]; this.filters.endDate = data[1] },

    refreshAll() {
      this.initOrderStatusChart();
      this.initLocationStatusChart();
      this.initExpiryDateItemsChart();
    },

    getChartColor(idx) { return CHART_COLORS[idx % CHART_COLORS.length] },

    getEChartsInstance(refName) {
      const dom = this.$refs[refName];
      if (!dom) return null;
      if (!this.chartInstances[refName]) {
        this.chartInstances[refName] = echarts.init(dom[0] || dom);
      }
      return this.chartInstances[refName];
    },

    // 订单状态看板
    initOrderStatusChart() {
      this.$nextTick(() => {
        const chart = this.getEChartsInstance('orderStatusChart');
        if (!chart) return;
        chart.setOption({
          title: { show: false },
          tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
          grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
          xAxis: { type: 'category', data: [], axisLabel: { color: '#606266' }, axisLine: { lineStyle: { color: '#E4E7ED' } } },
          yAxis: { type: 'value', axisLabel: { color: '#909399' }, splitLine: { lineStyle: { color: '#F2F6FC', type: 'dashed' } } },
          series: [{
            data: [], type: 'bar', barMaxWidth: 40,
            itemStyle: { color: CHART_COLORS[0], borderRadius: [4, 4, 0, 0] },
            label: { show: true, position: 'top', color: '#606266', fontWeight: 600, fontSize: 12 },
          }]
        });
        this.refreshOrderStatusData();
      });
    },
    refreshOrderStatusData() {
      const localeId = sessionStorage.getItem('localeId');
      this.$wmsApi.globalOption.getOrdersByStatus({
        localeId, whId: this.filters.whId,
        startDate: this.filters.startDate, endDate: this.filters.endDate
      }).then(res => {
        const data = res.data || [];
        const labels = data.map(d => d['orderStatus']);
        const values = data.map(d => d['orderNums']);
        const chart = this.getEChartsInstance('orderStatusChart');
        if (chart) chart.setOption({ xAxis: { data: labels }, series: [{ data: values }] });
      });
    },

    // 库位利用率看板
    initLocationStatusChart() {
      this.$nextTick(() => {
        const chart = this.getEChartsInstance('locationStatusChart');
        if (!chart) return;
        chart.setOption({
          title: { show: false },
          tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, formatter: '{b}: {c}%' },
          grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
          xAxis: { type: 'category', data: [], axisLabel: { color: '#606266' }, axisLine: { lineStyle: { color: '#E4E7ED' } } },
          yAxis: { type: 'value', max: 100, axisLabel: { color: '#909399', formatter: '{value}%' }, splitLine: { lineStyle: { color: '#F2F6FC', type: 'dashed' } } },
          series: [{
            data: [], type: 'bar', barMaxWidth: 40,
            itemStyle: { color: CHART_COLORS[1], borderRadius: [4, 4, 0, 0] },
            label: { show: true, position: 'top', color: '#606266', fontWeight: 600, fontSize: 12, formatter: p => p.value + '%' },
          }]
        });
        this.refreshLocationStatusData();
      });
    },
    refreshLocationStatusData() {
      this.$wmsApi.globalOption.getLocatonsByZone({ whId: this.filters.whId }).then(res => {
        const data = res.data || [];
        const labels = data.map(d => d['zoneDescription']);
        const values = data.map(d => parseFloat(d['locations']));
        const chart = this.getEChartsInstance('locationStatusChart');
        if (chart) chart.setOption({ xAxis: { data: labels }, series: [{ data: values }] });
      });
    },

    // 临期产品数量看板
    initExpiryDateItemsChart() {
      this.$nextTick(() => {
        const chart = this.getEChartsInstance('expiryDateItemsChart');
        if (!chart) return;
        chart.setOption({
          title: { show: false },
          tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
          grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
          xAxis: { type: 'category', data: [], axisLabel: { color: '#606266' }, axisLine: { lineStyle: { color: '#E4E7ED' } } },
          yAxis: { type: 'value', axisLabel: { color: '#909399' }, splitLine: { lineStyle: { color: '#F2F6FC', type: 'dashed' } } },
          series: [{
            data: [], type: 'bar', barMaxWidth: 40,
            itemStyle: { color: CHART_COLORS[2], borderRadius: [4, 4, 0, 0] },
            label: { show: true, position: 'top', color: '#606266', fontWeight: 600, fontSize: 12 },
          }]
        });
        this.refreshExpiryDateItems();
      });
    },
    refreshExpiryDateItems() {
      this.$wmsApi.globalOption.getExpiryDateItemsByZone({ whId: this.filters.whId }).then(res => {
        const data = res.data || [];
        const labels = data.map(d => d['zoneDescription']);
        const values = data.map(d => parseFloat(d['nums']));
        const chart = this.getEChartsInstance('expiryDateItemsChart');
        if (chart) chart.setOption({ xAxis: { data: labels }, series: [{ data: values }] });
      });
    },

    disposeCharts() {
      Object.keys(this.chartInstances).forEach(key => {
        this.chartInstances[key].dispose();
      });
      this.chartInstances = {};
    }
  },
  mounted() {
    this.refreshAll();
    this.intervals.push(setInterval(() => this.refreshOrderStatusData(), 60000));
    this.intervals.push(setInterval(() => this.refreshLocationStatusData(), 60000));
    this.intervals.push(setInterval(() => this.refreshExpiryDateItems(), 60000));
  },
  beforeDestroy() {
    this.intervals.forEach(id => clearInterval(id));
    this.disposeCharts();
  }
}
</script>

<style scoped>
.dashboard {
  padding: 16px;
  background: #f0f2f5;
  min-height: calc(100vh - 84px);
}

/* 工具栏卡片 */
.toolbar-card {
  margin-bottom: 16px;
  border-radius: 8px;
}
.toolbar-card >>> .el-card__body {
  padding: 12px 20px;
}
.toolbar-inner {
  display: flex;
  align-items: center;
}
.toolbar-inner .el-form {
  margin-bottom: 0;
}
.toolbar-inner .el-form-item {
  margin-bottom: 0;
}

/* 报表网格 - 2列布局 */
.chart-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

/* 报表卡片 */
.chart-card {
  border-radius: 8px;
  transition: transform 0.2s, box-shadow 0.2s;
}
.chart-card:hover {
  transform: translateY(-2px);
}
.chart-card >>> .el-card__header {
  padding: 12px 20px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
  border-radius: 8px 8px 0 0;
}
.chart-card >>> .el-card__body {
  padding: 12px;
}

.chart-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.chart-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}
.chart-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.chart-box {
  width: 100%;
  height: 280px;
}

/* 占位卡片 */
.placeholder-card {
  border: 2px dashed #d9d9d9;
  background: #fafafa;
}
.placeholder-card >>> .el-card__header {
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
}
.placeholder-card:hover {
  border-color: #c0c4cc;
}
.placeholder-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 280px;
  color: #c0c4cc;
}
.placeholder-icon {
  font-size: 48px;
  margin-bottom: 8px;
}
.placeholder-text {
  font-size: 14px;
  color: #c0c4cc;
  margin: 0;
}

/* 响应式 - 小屏幕单列 */
@media (max-width: 768px) {
  .chart-grid {
    grid-template-columns: 1fr;
  }
  .dashboard {
    padding: 12px;
  }
  .chart-box {
    height: 240px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/Wms/Dashboard.vue